import { useCallback } from 'react';
import type { Locale } from '@ili/shared';
import { usePrefs } from '../lib/prefs';

/**
 * UI labels live in i18n/<lang>/<namespace>.json and are looked up as t('namespace.key').
 * English is the fallback. Adding Ilocano = add i18n/ilo/*.json and 'ilo' to LOCALES.
 * Content written by offices (posts, notes) is not translated.
 */
type Dict = Record<string, unknown>;
const files = import.meta.glob('./*/*.json', { eager: true, import: 'default' }) as Record<string, Dict>;
const dicts: Record<string, Record<string, Dict>> = {};
for (const [path, data] of Object.entries(files)) {
  const [, lang, ns] = path.match(/^\.\/([a-z]+)\/([a-zA-Z0-9_-]+)\.json$/)!;
  (dicts[lang] ??= {})[ns] = data;
}

function lookup(lang: string, key: string): string | undefined {
  const [ns, ...rest] = key.split('.');
  let node: unknown = dicts[lang]?.[ns];
  for (const part of rest) node = (node as Dict | undefined)?.[part];
  return typeof node === 'string' ? node : undefined;
}

export type TVars = Record<string, string | number>;
export function translate(lang: Locale, key: string, vars?: TVars): string {
  let s = lookup(lang, key) ?? lookup('en', key);
  if (s === undefined) {
    if (import.meta.env.DEV) console.warn(`[i18n] missing key ${key}`);
    s = key.split('.').pop()!;
  }
  return vars ? s.replace(/\{(\w+)\}/g, (_, k) => (vars[k] === undefined ? `{${k}}` : String(vars[k]))) : s;
}

export function useT() {
  const { language } = usePrefs();
  return useCallback((key: string, vars?: TVars) => translate(language, key, vars), [language]);
}
export type T = ReturnType<typeof useT>;
